import React, { useState, useEffect, useCallback, startTransition } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BackHeader from '../../components/BackHeader';
import NavigationBar from '../../components/NavigationBar';
import NaverMap from '../../components/NaverMap';
import { useAddress } from '../../contexts/AddressContext';
import { getCurrentPosition, reverseGeocode, getIpBasedLocation } from '../../services/LocationService';
import { showChannelTalk, trackChannelTalkEvent } from '../../services/ChannelService';
import { createAddress, CreateAddressRequest } from '../../api/addressService';
import useTranslation from '../../hooks/useTranslation';
import { getCurrentLanguage } from '../../config/languageConfig';
import AddressFormFields from './components/AddressFormFields';
import AddressLines from './components/AddressLines';
import { useDisplayAddress } from '../../hooks/useDisplayAddress';

const AddressSetupGPS: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { address, setAddress } = useAddress();
  const displayLines = useDisplayAddress(address);
  const { t } = useTranslation();
  
  // GPS 전용 상태들
  const [mainAddress, setMainAddress] = useState('');
  const [detailAddress, setDetailAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [touched, setTouched] = useState({
    mainAddress: false,
    detailAddress: false
  });
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showMap, setShowMap] = useState(false);
  
  // 원본 주소 정보 저장 (Cancel 기능을 위해)
  const [, setOriginalAddress] = useState({
    mainAddress: '',
    detailAddress: '',
    location: null as { lat: number; lng: number } | null
  });
  
  // 편집 모드 상태
  const [isEditMode, setIsEditMode] = useState(false);
  
  // URL 파라미터에서 편집 모드 결정
  useEffect(() => {
    const mode = searchParams.get('mode');
    const hasAddress = address && address.isComplete;
    
    if (!hasAddress) {
      setIsEditMode(true);
    } else {
      setIsEditMode(mode === 'edit');
    }
  }, [address, searchParams]);

  // 기존 주소 정보 로드
  useEffect(() => {
    if (address) {
      // 뷰모드에서 stay의 display_info가 있으면 우선 사용, 없으면 mainAddress 사용
      const displayAddress = !isEditMode && address.stay?.display_info 
        ? address.stay.display_info 
        : address.mainAddress || '';
        
      setMainAddress(displayAddress);
      setDetailAddress(address.detailAddress || '');
      
      if (address.lat !== undefined && address.lng !== undefined) {
        const location = { lat: address.lat, lng: address.lng };
        setCurrentLocation(location);
        setShowMap(true);
        setOriginalAddress({
          mainAddress: address.mainAddress || '', // 원본은 항상 mainAddress 저장
          detailAddress: address.detailAddress || '',
          location: location
        });
      } else {
        setOriginalAddress({
          mainAddress: address.mainAddress || '', // 원본은 항상 mainAddress 저장
          detailAddress: address.detailAddress || '',
          location: null
        });
      }
    }
  }, [address, isEditMode]); // isEditMode 의존성 추가

  // 편집모드로 전환될 때 지도 표시 보장
  useEffect(() => {
    if (isEditMode && currentLocation && !showMap) {
      setShowMap(true);
    }
  }, [isEditMode, currentLocation]); // showMap 의존성 제거

  // 디버깅: 지도 표시 상태 추적
  useEffect(() => {
  }, [showMap, currentLocation]); // isEditMode, address 의존성 제거

  // GPS 위치 가져오기
  const handleUseGPS = useCallback(async () => {
    try {
      setIsLoading(true);
      setLocationError(null);
      
      // 브라우저의 Geolocation API를 사용하여 현재 위치 좌표 가져오기
      const position = await getCurrentPosition().catch(async (error) => {
        console.error('Geolocation API 오류:', error);
        
        // Geolocation 실패 시 IP 기반 위치 정보 사용
        if ((error as GeolocationPositionError)?.code === 1) {
          setLocationError(t('address.gps.permissionDenied'));
          const ipLocation = await getIpBasedLocation();
          if (!ipLocation) throw error;
          return ipLocation;
        } else if ((error as GeolocationPositionError)?.code === 2) {
          setLocationError(t('address.gps.unavailable'));
          const ipLocation = await getIpBasedLocation();
          if (!ipLocation) throw error;
          return ipLocation;
        } else {
          throw error;
        }
      });
      
      // 위치 정보 저장
      setCurrentLocation(position);
      
      // 약간의 지연 후 지도 표시
      setTimeout(() => {
        setShowMap(true);
      }, 500);
      
      // 현재 언어 설정에 따라 영문 주소 선호 여부 결정
      const currentLang = getCurrentLanguage();
      const preferEnglish = ['en', 'ja', 'zh-CN', 'zh-TW'].includes(currentLang);
      
      // 네이버 API를 우선 사용하여 좌표를 주소로 변환
      const geoAddress = await reverseGeocode(position.lng, position.lat, preferEnglish);
      
      // 상태 업데이트
      startTransition(() => {
        if (geoAddress) {
          setMainAddress(geoAddress);
          setTouched(prev => ({ ...prev, mainAddress: true }));
        } else {
          setMainAddress(`Latitude: ${position.lat.toFixed(6)}, Longitude: ${position.lng.toFixed(6)}`);
          setLocationError(t('address.gps.coordinatesOnly'));
        }
      });
    } catch (error) {
      console.error('위치 정보 가져오기 실패:', error);
      setLocationError(t('address.gps.failed'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);
  
  // GPS 자동 실행 (새 사용자 또는 편집 모드일 때)
  useEffect(() => {
    const hasExistingAddress = address && address.isComplete;
    
    // 기존 주소가 없고, 편집 모드이며, 현재 위치가 없을 때만 GPS 자동 실행
    if (isEditMode && !currentLocation && !hasExistingAddress) {
      const timer = setTimeout(() => {
        handleUseGPS();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isEditMode, currentLocation, handleUseGPS, address]);

  // 유효성 검사
  const validateMainAddress = (address: string): { isValid: boolean; message: string | null } => {
    const trimmedAddress = address.trim();
    
    if (trimmedAddress.length === 0) {
      return { isValid: false, message: t('address.required') };
    }
    
    if (trimmedAddress.length < 8) {
      return { isValid: false, message: t('address.tooShort') };
    }
    
    if (!/\d/.test(trimmedAddress)) {
      return { isValid: false, message: t('address.missingNumber') };
    }
    
    return { isValid: true, message: null };
  };

  const mainAddressValidation = validateMainAddress(mainAddress);
  const isMainAddressValid = mainAddressValidation.isValid;
  const isDetailAddressValid = detailAddress.trim().length > 0;
  const isFormValid = isMainAddressValid && isDetailAddressValid;

  // 지도에서 위치 선택 시
  const handleLocationSelect = useCallback(async (location: { lat: number; lng: number }) => {
    setCurrentLocation(location);
    
    try {
      const currentLang = getCurrentLanguage();
      const preferEnglish = ['en', 'ja', 'zh-CN', 'zh-TW'].includes(currentLang);
      
      const geoAddress = await reverseGeocode(location.lng, location.lat, preferEnglish);
      
      if (geoAddress) {
        setMainAddress(geoAddress);
      } else {
        const coordsText = `Latitude: ${location.lat.toFixed(5)}, Longitude: ${location.lng.toFixed(5)}`;
        setMainAddress(coordsText);
      }
      
      setTouched(prev => ({ ...prev, mainAddress: true }));
    } catch (error) {
      console.error('선택한 위치의 주소 변환 실패:', error);
      const coordsText = `Latitude: ${location.lat.toFixed(5)}, Longitude: ${location.lng.toFixed(5)}`;
      setMainAddress(coordsText);
    }
  }, []);

  // 필드 변경 핸들러 (GPS 모드에서는 mainAddress 변경 불가)
  const handleMainAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // GPS 모드에서는 주소 수정 불가
    return;
  };

  const handleDetailAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDetailAddress(e.target.value);
    setTouched(prev => ({ ...prev, detailAddress: true }));
  };

  // 주소 저장
  const handleSaveAddress = async () => {
    setTouched({ mainAddress: true, detailAddress: true });

    const isGpsCoordinates = mainAddress.includes('Latitude:') && mainAddress.includes('Longitude:');
    
    if (!isGpsCoordinates && !isFormValid) {
      return;
    }

    setIsLoading(true);

    try {
      if (currentLocation) {
        const addressRequest: CreateAddressRequest = {
          address: mainAddress.trim(),
          detail_address: detailAddress.trim(),
          lat: currentLocation.lat.toString(),
          lng: currentLocation.lng.toString()
        };

        const response = await createAddress(addressRequest);

        setAddress({
          mainAddress: mainAddress.trim(),
          detailAddress: detailAddress.trim(),
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          addressId: response.id,
          isComplete: true,
          selectedMethod: 'gps'
        });
      } else {
        setAddress({
          mainAddress: mainAddress.trim(),
          detailAddress: detailAddress.trim(),
          isComplete: true,
          selectedMethod: 'gps'
        });
      }

      navigate('/');
    } catch (error) {
      console.error('Error saving address:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Back to Preference 핸들러
  const handleBackToPreference = () => {
    navigate('/address/method');
  };

  // Edit Address 핸들러
  const handleEditAddress = () => {
    // URL 파라미터에 mode=edit 추가하여 편집모드로 전환
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('mode', 'edit');
    navigate(`/address?${newSearchParams.toString()}`, { replace: true });
  };

  // Confirm Address 핸들러
  const handleConfirmAddress = () => {
    navigate('/');
  };

  // 채널톡 도움말
  const handleOpenHelp = () => {
    trackChannelTalkEvent('address_help_requested', {
      mainAddress: mainAddress,
      hasDetailAddress: !!detailAddress.trim(),
      method: 'gps'
    });
    
    if (window.ChannelIO) {
      try {
        window.ChannelIO('boot', {
          "pluginKey": "4de468b6-e6bf-4446-a6d0-fb3ccb1132a6",
          "language": getCurrentLanguage(),
          "hideDefaultLauncher": true,
          "customLauncherSelector": "#custom-channel-button" 
        });
        
        setTimeout(() => {
          showChannelTalk();
        }, 500);
      } catch (error) {
        console.error('Failed to initialize ChannelTalk on help request:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <BackHeader title={t('address.setup')} />
      
      {/* Stay 정보 배너 */}
      {address?.stay && (
        <div className="mx-4 mt-4">
          <div className="bg-green-100 border border-green-200 rounded-lg p-2 mt-3">
            <div className="flex items-center justify-center mb-1">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <h3 className="text-xl font-bold text-green-800 tracking-wide">
                {t('address.stay.banner.title')}
              </h3>
            </div>
            <p className="text-xs text-green-700 leading-relaxed text-center">
              {t('address.stay.banner.subtitle')}
            </p>
          </div>
        </div>
      )}
      
      <div className="flex-1 px-4 py-2 pb-48">
        <div className="rounded-lg border border-gray-200 p-4 mb-2 bg-white shadow-sm">
          {isEditMode ? (
            <AddressFormFields
              mainAddress={mainAddress}
              detailAddress={detailAddress}
              onMainAddressChange={handleMainAddressChange}
              onDetailAddressChange={handleDetailAddressChange}
              isEditMode={isEditMode}
              isMainAddressReadOnly={true} // GPS 모드에서는 항상 read-only
              touched={touched}
              validation={{
                isMainAddressValid,
                mainAddressMessage: mainAddressValidation.message,
                isDetailAddressValid
              }}
              currentLocation={currentLocation}
              locationError={locationError}
            />
          ) : (
            <AddressLines display={displayLines} className="py-1" showFormatInfo />
          )}

          {/* Use GPS Button - 편집 모드일 때만 표시 */}
          {isEditMode && (
            <div className="mb-5">
              <button 
                type="button"
                onClick={handleUseGPS}
                disabled={isLoading}
                className={`w-full flex items-center justify-center gap-1 py-3 rounded-lg ${
                  isLoading 
                    ? 'bg-gray-200 cursor-not-allowed' 
                    : 'bg-gray-100 hover:bg-gray-200 active:bg-gray-300 cursor-pointer'
                } transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-300`}
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
                <span className="text-gray-700">{isLoading ? t('common.loading') : t('address.useGPS')}</span>
              </button>
            </div>
          )}
        </div>

        {/* 지도 표시 */}
        {showMap && currentLocation && (
          <div className="mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              <NaverMap 
                key={`gps-map-${currentLocation.lat}-${currentLocation.lng}`}
                initialCenter={currentLocation}
                onLocationSelect={isEditMode ? handleLocationSelect : undefined}
                height="300px"
                zoom={19}
                markerDraggable={isEditMode}
              />
            </div>
          </div>
        )}
        
        {/* 버튼 영역 */}
        <div className="mt-6">
          {isEditMode ? (
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleSaveAddress}
                disabled={isLoading || (!isFormValid && !mainAddress.includes('Latitude:'))}
                className={`w-full py-3 rounded-lg font-semibold text-white ${
                  isLoading || (!isFormValid && !mainAddress.includes('Latitude:'))
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-500 hover:bg-red-600 active:bg-red-700'
                } transition-all duration-150 shadow-sm`}
              >
                {t('address.saveChanges')}
              </button>
              <button
                type="button"
                onClick={handleBackToPreference}
                className="w-full py-3 rounded-lg font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 shadow-sm"
              >
                {t('address.backToPreference')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleConfirmAddress}
                className="w-full py-3 rounded-lg font-semibold text-white bg-red-500 hover:bg-red-600 active:bg-red-700 transition-all duration-150 shadow-sm"
              >
                {t('address.confirm')}
              </button>
              <button
                type="button"
                onClick={handleEditAddress}
                className="w-full py-3 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 transition-all duration-150 shadow-sm"
              >
                {t('address.edit')}
              </button>
            </div>
          )}
        </div>
        
        {/* Help link */}
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={handleOpenHelp}
            className="text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            {t('address.help')}
          </button>
        </div>
      </div>
      
      <NavigationBar />
    </div>
  );
};

export default AddressSetupGPS;
